import { assertNever } from '@kofno/piper';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useCallback } from 'react';
import { translation, TranslationsContext } from '../../../../../Translations';
import CloseIcon from '../../../../Cutesies/CloseIcon';
import { shareStore } from '../Stores/ShareStore';
import { Participant } from '../Stores/ShareStore/Types';

// Chip colour answers one question: will this person be invited. Verified is
// the primary colour, not-found and unlicensed are both destructive (the two
// are still indistinguishable here, see the design note on DVLP-7740), and
// pending is muted.
const participantChipClasses = (participant: Participant): string => {
  const status = participant.status;
  switch (status) {
    case 'verified':
      return 'border border-primary bg-card text-primary';
    case 'not-found':
    case 'unlicensed':
      return 'border border-destructive bg-card text-destructive';
    case 'pending':
      return 'border border-border bg-card text-muted-foreground';
    default:
      return assertNever(status);
  }
};

const removeButtonClasses = (participant: Participant): string => {
  const status = participant.status;
  switch (status) {
    case 'verified':
      return 'bg-primary hover:bg-primary/80';
    case 'not-found':
    case 'unlicensed':
      return 'bg-destructive hover:bg-destructive/80';
    case 'pending':
      return 'bg-muted-foreground hover:bg-muted-foreground/80';
    default:
      return assertNever(status);
  }
};

const ParticipantTextArea: React.FC = () => {
  const ts = React.useContext(TranslationsContext);
  const inputRef = React.useRef<HTMLDivElement>(null);

  const handleContainerClick = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const text = event.currentTarget.textContent?.trim() || '';
      if (text) {
        shareStore.addParticipants(text);
        event.currentTarget.textContent = '';
      }
    }
  }, []);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const text = event.currentTarget.textContent?.trim() || '';
    if (text) {
      shareStore.addParticipants(text);
      event.currentTarget.textContent = '';
    }
  }, []);

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text/plain');
    if (pastedText.trim()) {
      shareStore.addParticipants(pastedText);
      event.currentTarget.textContent = '';
    }
  }, []);

  const renderParticipantTag = (participant: Participant, index: number) => {
    const displayName =
      participant.kind === 'user'
        ? 'name' in participant
          ? participant.name
          : participant.email
        : participant.name;

    const showMemberCount =
      participant.kind === 'group' && 'members' in participant && participant.status === 'verified';

    const fullDisplayName = showMemberCount
      ? `${displayName} (${participant.members.length} members)`
      : displayName;

    return (
      <div
        key={`${participant.kind}-${index}`}
        className={clsx(
          'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm flex-shrink-0 max-w-full',
          participantChipClasses(participant),
        )}
      >
        <span className="truncate">{fullDisplayName}</span>
        <button
          type="button"
          onClick={() => shareStore.removeParticipant(participant)}
          className={clsx(
            'flex items-center justify-center w-4 h-4 rounded transition-colors [&_svg]:fill-primary-foreground [&_svg]:stroke-primary-foreground',
            removeButtonClasses(participant),
          )}
          aria-label={`${translation('Remove')(ts)} ${displayName}`}
          data-intercom-target="share-participant-chip-remove"
        >
          <CloseIcon variant="small" />
        </button>
      </div>
    );
  };

  return (
    <div
      onClick={handleContainerClick}
      className="w-full border border-border rounded-md p-3 bg-card focus-within:ring-2 focus-within:ring-ring focus-within:border-ring min-h-[80px] flex flex-wrap gap-2 items-start cursor-text"
      data-intercom-target="share-participant-chips"
      style={{ width: '100%', maxWidth: '100%' }}
    >
      {shareStore.participants.map((participant, index) =>
        renderParticipantTag(participant, index),
      )}
      {/* Input */}
      <div
        ref={inputRef}
        contentEditable
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onPaste={handlePaste}
        data-placeholder={
          shareStore.participants.length === 0 ? translation('No participants added yet')(ts) : ''
        }
        className="flex-1 min-w-[200px] outline-none text-sm py-1 empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground"
        suppressContentEditableWarning
      />
    </div>
  );
};

export default observer(ParticipantTextArea);
